import { noOp } from 'common/Constants';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Portion } from 'src/shared/design-system/DishFirst/Portion';
import { FaSpinnerThird } from 'src/shared/design-system/FontAwesome';
import { ExclamationTriangle, Plus } from 'src/shared/design-system/Icon';
import { ifProp } from 'src/shared/styled-utils/ifProp';
import { minScreen } from 'src/shared/utils/StyledComponentUtils';
import styled, { css, keyframes } from 'styled-components';
import { Border, Colors, Font, ScreenWidth, Spacing, Transition } from 'web-common/src/shared/styles';
import { QuickAddProps } from './types';

const Container = styled.div`
  display: flex;
  flex-direction: row;
`;

const spin = keyframes`
  from { 
    transform:rotate(0deg); 
  }
  to { 
    transform:rotate(360deg);
  }
`;

const QuantityButton = styled.button<{ clicked: boolean; isLoading: boolean; error: boolean }>`
  border: none;
  border-radius: ${Border.Radius.M};
  background-color: ${(props) => (props.isLoading || props.error ? Colors.BELL_PEPPER_LITE_30 : Colors.RASPBERRY)};
  color: ${Colors.RASPBERRY};
  display: flex;
  justify-content: center;
  align-items: center;
  height: 24px;
  width: 24px;
  ${minScreen(ScreenWidth.SMALL)} {
    height: 32px;
    width: 32px;
  }
  margin: ${(props) => (props.isLoading || props.error ? `-${Spacing.QUARTER}` : '0')};

  // MVP Click effect (NOTE: Would like to improve on tactile feedback later)
  transition: transform ${Transition.Time.HALF} ease-in-out;
  ${ifProp(
    'clicked',
    css`
      transform: scale(0.85);
    `
  )}

  & svg {
    width: 18px;
    height: 18px;
    animation: 1500ms linear infinite ${spin} ${(props) => (props.isLoading ? 'running' : 'paused')};
  }
`;

export const PortionPicker = styled(Portion).attrs(() => ({
  autoHide: true,
  minimizeOnZeroQuantity: true,
  preventDefaultOnClick: true,
}))`
  border: none !important;
  margin: -${Spacing.HALF};

  & button {
    padding: ${Spacing.QUARTER};

    & > div {
      border-radius: ${Border.Radius.M};

      & > span {
        font-size: ${Font.Size.S};
        font-weight: ${Font.Weight.SEMI_BOLD};
      }
    }
  }
`;

export const DishFirstQuickAdd: React.FC<QuickAddProps> = (props) => {
  const { decrement, increment = noOp, minQuantity, maxQuantity, quantity, loading = false, error = false } = props;
  const [clicked, setClicked] = useState(false);
  const clickedRef = useRef(0);

  // Only show full selector if decrement function is shown.
  const showFullPortionPicker = !!decrement && !loading && !error;

  useEffect(() => () => {
    clearTimeout(clickedRef.current);
  });

  // Separate so that users can harmlessly click on other portions of this container when built
  const preventDefault = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent.stopImmediatePropagation();
  }, []);

  const handleDecrement = useCallback(() => {
    decrement?.();
  }, [decrement]);

  const handleIncrement = useCallback(() => {
    // Click effect
    if (!showFullPortionPicker) {
      setClicked(true);
      clickedRef.current = window.setTimeout(() => setClicked(false), 160);
    }

    increment();
  }, [increment]);

  const portionPicker = useMemo(
    () => (
      <PortionPicker
        incrementQuantity={handleIncrement}
        decrementQuantity={handleDecrement}
        quantity={quantity}
        minQuantity={minQuantity}
        maxQuantity={maxQuantity}
      />
    ),
    [handleIncrement, handleDecrement, quantity, minQuantity, maxQuantity]
  );

  return (
    <Container onClick={preventDefault} onMouseDown={preventDefault}>
      {showFullPortionPicker ? (
        portionPicker
      ) : (
        <QuantityButton onClick={handleIncrement} clicked={clicked} isLoading={loading} error={error}>
          {error && <ExclamationTriangle />}
          {loading && <FaSpinnerThird />}
          {!loading && !error && <Plus circleFill={Colors.RASPBERRY} symbolFill={Colors.WHITE} />}
        </QuantityButton>
      )}
    </Container>
  );
};
